import type { Theme } from '@mui/material/styles';
import { density } from './tokens';

/**
 * The Data Grid's surface treatment (CLAUDE.md rule 4: density, no card-per-field layouts).
 *
 * Density is a theme decision — `MuiDataGrid.defaultProps` in theme.ts carries row height and
 * compact mode — but the surface rules below cannot be expressed as MUI X slot overrides without
 * duplicating them, so grids still spread this one `sx`. Grids therefore look identical across
 * modules without each one re-deciding.
 *
 * Every variant-specific value is read from `theme.eis.surface.grid` (prompt 24 Phase 24.3), so
 * one object serves both variants: `berry` sits the grid flush inside a white table card — white
 * header band, 12.5px/600 titles in `text.secondary`, a divider under the header, hairline rows —
 * while `suite` keeps prompt 22's own bordered, rounded frame with the sunken header band.
 */
const grid = (theme: Theme): Theme['eis']['surface']['grid'] => theme.eis.surface.grid;

export const dataGridSx = {
  border: (theme: Theme) => (grid(theme).frame ? 1 : 0),
  borderColor: 'divider',
  borderRadius: (theme: Theme) =>
    grid(theme).frame ? `${String(theme.eis.surface.cardRadius)}px` : 0,
  backgroundColor: 'var(--mui-palette-background-paper)',
  '--DataGrid-rowBorderColor': (theme: Theme) => grid(theme).rowBorder,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (theme: Theme) => grid(theme).headerBackground,
    // Flush in a card, the header needs its own rule: the rows below are only hairlines.
    '& .MuiDataGrid-columnHeader': {
      borderBottomColor: (theme: Theme) =>
        grid(theme).frame ? undefined : 'var(--mui-palette-divider)',
    },
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: (theme: Theme) => grid(theme).headerFontSize,
    color: (theme: Theme) => grid(theme).headerColor,
    whiteSpace: 'normal',
    lineHeight: 1.2,
    overflow: 'visible',
    textOverflow: 'clip',
  },
  '& .MuiDataGrid-cell': {
    fontSize: (theme: Theme) => grid(theme).cellFontSize,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: '2px solid var(--mui-palette-primary-main)',
    outlineOffset: -2,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'var(--mui-palette-action-hover)',
  },
  '& .MuiDataGrid-footerContainer': { minHeight: density.toolbarHeight },
  '& .MuiDataGrid-overlayWrapper': { minHeight: 180 },
} as const;

/**
 * Props every grid in the console shares, so density never drifts between screens. The row
 * height is NOT here: it is the variant's (`MuiDataGrid.defaultProps` — 44 in berry, 40 in suite).
 */
export const dataGridDefaults = {
  density: 'compact',
  columnHeaderHeight: density.gridHeaderHeight,
  disableRowSelectionOnClick: true,
  disableColumnMenu: false,
  showToolbar: false,
} as const;
